
import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject, DOCUMENT, PLATFORM_ID, Inject } from '@angular/core';
import { ChildActivationEnd, Event, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, Subject, take, takeUntil } from 'rxjs';
import { AFFILIATE, LOCALE, languagesIds } from './affiliate.constants';
import { AffiliateCookie, AffiliateResponse } from './affiliate.model';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@www-src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AffiliateService implements OnDestroy {
  private router = inject(Router);
  private http = inject(HttpClient);
  private document = inject(DOCUMENT);
  private cookieService = inject(CookieService);
  private destroy$: Subject<void> = new Subject<void>();
  public isAffiliateBannerShown$ = new BehaviorSubject<boolean>(false);
  public affiliateName$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  private readonly languages: string[] = languagesIds;
  private affiliateName: string | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.setRouterEventsHandler();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setRouterEventsHandler(): void {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(e => this.routerHandler(e));
  }

  private getAffiliateByName(name: string): Observable<AffiliateResponse> {
    return this.http.get<AffiliateResponse>(`${environment.ACCOUNT_API_URL}/v1/affiliates/${name}`);
  }

  private handleGetAffiliate(): void {
    this.getAffiliateByName(this.affiliateName).pipe(take(1)).subscribe({
      next: (res) => {
        this.affiliateName = null;
        this.affiliateName$.next(res.data.name);
        this.isAffiliateBannerShown$.next(true);
        this.setAffiliateToCookies({
          affiliate: res.data.affiliate,
          name: res.data.name
        });
      },
      error: () => {
        this.affiliateName = null;
        !(this.router.url === '/') && this.router.navigateByUrl('/');
        this.isAffiliateBannerShown$.next(false);
      }
    });
  }

  private routerHandler(e: Event): void {
    if (
      e instanceof ChildActivationEnd
      && LOCALE in e.snapshot.params
      && !this.isLanguageExist(e.snapshot.params[LOCALE])
    ) {
      const locale = this.router.url.split('/')[1];
      this.affiliateName = e.snapshot.params[LOCALE];
      this.router.navigate([this.router.url.replace(`/${locale}`, '')]);
      this.handleGetAffiliate();
    }
  }

  private setAffiliateToCookies(affiliate: AffiliateCookie): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.cookieService.set(AFFILIATE, JSON.stringify(affiliate), { expires: 400, domain: this.document.defaultView.location.hostname.split(/www|account/)[1] });
  }

  public getAffiliateFromCookie(): AffiliateCookie | false {
    const affiliateCookie: string = this.cookieService.get(AFFILIATE);
    const isStartsLikeObject = affiliateCookie.startsWith('{') && affiliateCookie.endsWith('}');

    if (isStartsLikeObject) {
      const affiliate: AffiliateCookie = JSON.parse(affiliateCookie);
      return affiliate?.affiliate && affiliate?.name ? affiliate : false;
    }

    return false;
  }

  public setAffiliateBannerOpen(state: boolean): void {
    this.isAffiliateBannerShown$.next(state);
  }

  public redirectToAccount(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    
    this.document.location.href = `${environment.ACCOUNT_CLIENT_URL}/apps/create`;
  }

  public getAffiliateName(): string {
    return this.affiliateName$.getValue();
  }

  public newTabWithAffiliate(url: string, target: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const affiliate = this.getAffiliateFromCookie();
    if (affiliate) {
      this.document.defaultView.open(affiliate ? url + `?affiliate=${affiliate.affiliate}` : url, target);
      return;
    }

    this.document.defaultView.open(url, target);
  }

  public isLanguageExist(name: string): boolean {
    return this.languages.includes(name);
  }
}
